import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios from "../../config/axios";
import Loading from "components/Loading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Tabs,
  Tab,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  TextField,
  IconButton,
} from "@material-ui/core";
import { FileCopy as CopyIcon, Assignment as UseConfigIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
  table: {
    marginTop: theme.spacing(2),
  },
  purgeButton: {
    marginBottom: theme.spacing(2),
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "center",
  },
  formControl: {
    minWidth: 180,
  },
  productCard: {
    marginBottom: theme.spacing(2),
  },
  createPriceForm: {
    maxWidth: 640,
    marginTop: theme.spacing(2),
  },
  tierRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tierSection: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    alignItems: "flex-start",
  },
  tierExample: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    minWidth: 280,
    fontSize: "0.8rem",
  },
  tierExampleTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  priceNote: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  archivedRow: {
    opacity: 0.7,
    backgroundColor: theme.palette.action.hover,
  },
  tierChip: {
    display: "inline-block",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  tierTableWrap: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    overflow: "auto",
  },
  tierTable: {
    fontSize: "0.8rem",
    minWidth: 200,
    "& th, & td": {
      padding: "2px 8px 2px 0",
      textAlign: "left",
    },
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  actionCell: {
    whiteSpace: "nowrap",
  },
  intervalSection: {
    marginBottom: theme.spacing(3),
    "&:last-child": { marginBottom: 0 },
  },
  intervalSectionTitle: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: theme.palette.primary.contrastText || "#fff",
    backgroundColor: theme.palette.primary.main,
    textAlign: "center",
    padding: theme.spacing(1.5, 2),
  },
}));

function TabPanel({ children, value, index, className }) {
  return (
    <div role="tabpanel" hidden={value !== index} className={className}>
      {value === index && <Box p={0}>{children}</Box>}
    </div>
  );
}

const FREQUENCIES = ["monthly", "quarterly", "semester", "annual"];

const STRIPE_INTERVAL_TO_FREQUENCY = {
  1: "monthly",
  3: "quarterly",
  6: "semester",
  12: "annual",
};

const getFrequencyFromStripePrice = (price) => {
  const count = Number(price?.recurring?.interval_count);
  const interval = (price?.recurring?.interval || "").toLowerCase();
  if (interval === "year" && (count === 1 || Number.isNaN(count))) return "annual";
  if (interval === "month" && !Number.isNaN(count)) return STRIPE_INTERVAL_TO_FREQUENCY[count] || "monthly";
  return "monthly";
};

// Opção B — preços mensais c/IVA incluído (tax_behavior: inclusive)
const SPACE_DEFAULT_TIERS = [
  { up_to_m2: "3.0", price_per_m2_euro: "35" },
  { up_to_m2: "5.0", price_per_m2_euro: "33" },
  { up_to_m2: "7.0", price_per_m2_euro: "30" },
  { up_to_m2: "10.0", price_per_m2_euro: "25" },
  { up_to_m2: "15.0", price_per_m2_euro: "23" },
  { up_to_m2: "20.0", price_per_m2_euro: "20" },
  { up_to_m2: "25.0", price_per_m2_euro: "18" },
  { up_to_m2: "", price_per_m2_euro: "13" },
];

const StripePricing = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [products, setProducts] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [purging, setPurging] = useState(false);

  const [saving, setSaving] = useState(false);

  const [createPriceForm, setCreatePriceForm] = useState({
    product: "",
    frequency: "monthly",
    unit_amount_euro: "",
    tiers: [{ up_to_m2: "", price_per_m2_euro: "" }],
  });
  const [creatingPrice, setCreatingPrice] = useState(false);
  const [lastCreatedPriceId, setLastCreatedPriceId] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const res = await axios.get("/admin/stripe/products");
      if (res.data && res.data.data) setProducts(res.data.data);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || t("stripePricing.errors.fetchProducts"), {
        variant: "error",
      });
    } finally {
      setLoadingProducts(false);
    }
  }, [enqueueSnackbar, t]);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoadingConfigs(true);
      const res = await axios.get("/admin/stripe/price-config");
      if (res.data && res.data.data) setConfigs(res.data.data);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || t("stripePricing.errors.fetchConfigs"), {
        variant: "error",
      });
    } finally {
      setLoadingConfigs(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    if (tab === 0 || tab === 2) fetchProducts();
    if (tab === 0 || tab === 1) fetchConfigs();
  }, [tab, fetchProducts, fetchConfigs]);

  const [listFilters, setListFilters] = useState({
    productId: "",
    active: "",
  });

  const handlePurgeCache = async () => {
    try {
      setPurging(true);
      await axios.post("/admin/stripe/price-config/purge-cache");
      enqueueSnackbar(t("stripePricing.success.cachePurged"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || t("stripePricing.errors.purgeCache"), {
        variant: "error",
      });
    } finally {
      setPurging(false);
    }
  };

  const priceOptions = products.flatMap((p) =>
    (p.prices || []).map((pr) => ({
      ...pr,
      productName: p.name,
      productId: p.id,
    })),
  );

  const isSpaceProduct = (productId) => {
    const p = products.find((pr) => pr.id === productId);
    const name = (p?.name || "").toLowerCase();
    const isSpace =
      name.includes("space") ||
      name.includes("m2") ||
      name.includes("m²") ||
      name.includes("armazenamento") ||
      name.includes("storage");
    if (productId && createPriceForm.product === productId) {

    }
    return isSpace;
  };

  const handleCreatePriceFormChange = (field, value) => {
    setCreatePriceForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "product" && value) {
        if (isSpaceProduct(value)) {
          next.tiers = SPACE_DEFAULT_TIERS.map((t) => ({ ...t }));
        } else {
          next.unit_amount_euro = "";
        }
      }
      return next;
    });
  };

  const handleTierChange = (index, field, value) => {
    setCreatePriceForm((prev) => {
      const next = { ...prev, tiers: [...prev.tiers] };
      next.tiers[index] = { ...next.tiers[index], [field]: value };
      return next;
    });
  };

  const handleCreatePrice = async () => {
    const { product, frequency, unit_amount_euro, tiers } = createPriceForm;
    if (!product) {
      enqueueSnackbar(t("stripePricing.errors.selectProduct"), { variant: "warning" });
      return;
    }
    const isSpace = isSpaceProduct(product);
    if (isSpace) {
      const filteredTiers = tiers.filter((t) => t.price_per_m2_euro !== "");
      if (filteredTiers.length === 0) {
        enqueueSnackbar(t("stripePricing.errors.addTier"), { variant: "warning" });
        return;
      }
    } else {
      const euro = parseFloat(unit_amount_euro);
      if (Number.isNaN(euro) || euro < 0) {
        enqueueSnackbar(t("stripePricing.errors.validUnitAmount"), { variant: "warning" });
        return;
      }
    }

    try {
      setCreatingPrice(true);
      setLastCreatedPriceId(null);
      const body = {
        product,
        currency: "eur",
        frequency,
        tax_behavior: "inclusive",
      };
      if (isSpace) {
        const filteredTiers = createPriceForm.tiers.filter((t) => t.price_per_m2_euro !== "");
        body.billing_scheme = "tiered";
        body.tiers = filteredTiers.map((t, i) => {
          const isLast = i === filteredTiers.length - 1;
          const upToM2 = parseFloat(t.up_to_m2);
          const pricePerM2 = parseFloat(t.price_per_m2_euro);
          const up_to = isLast ? "inf" : Math.round(upToM2 * 10);
          const unit_amount = Math.round(pricePerM2 * 10);
          return { up_to, unit_amount };
        });
      } else {
        body.billing_scheme = "per_unit";
        body.unit_amount = Math.round(parseFloat(unit_amount_euro) * 100);
      }
      const res = await axios.post("/admin/stripe/prices", body);
      const price = res.data?.data;
      setLastCreatedPriceId(price?.id || null);
      enqueueSnackbar(t("stripePricing.success.priceCreated", { priceId: price?.id }), { variant: "success" });
      fetchProducts();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || t("stripePricing.errors.createPrice"), { variant: "error" });
    } finally {
      setCreatingPrice(false);
    }
  };

  const formatPrice = (p) => {
    if (p.unit_amount != null) return `€${(p.unit_amount / 100).toFixed(2)}`;
    if (p.billing_scheme === "tiered" && p.tiers && p.tiers.length)
      return `Tiered (first: €${((p.tiers[0].unit_amount || 0) / 10).toFixed(2)}/m²)`;
    return "-";
  };

  const formatTieredTiers = (p) => {
    if (p.billing_scheme !== "tiered" || !p.tiers || !p.tiers.length) return [];
    let prevUpTo = 0;
    return p.tiers.map((tier) => {
      const upTo = tier.up_to === "inf" || tier.up_to == null ? null : Number(tier.up_to);
      const m2From = prevUpTo === 0 ? "1.0" : (prevUpTo / 10).toFixed(1);
      const m2To = upTo === null ? "∞" : (upTo / 10).toFixed(1);
      const eurPerM2 = ((tier.unit_amount || 0) / 10).toFixed(2);
      if (upTo !== null) prevUpTo = upTo + 1;
      return `${m2From}–${m2To} m²: €${eurPerM2}/m²`;
    });
  };

  const getConfigForPriceId = (priceId) => {
    return configs.find((c) => c.stripe_price_id === priceId);
  };

  const groupPricesByInterval = (prices) => {
    const groups = {};
    FREQUENCIES.forEach((f) => { groups[f] = []; });
    prices.forEach((p) => {
      const freq = getFrequencyFromStripePrice(p);
      if (groups[freq]) groups[freq].push(p);
      else groups[freq] = [p];
    });
    FREQUENCIES.forEach((f) => {
      if (groups[f]) groups[f].sort((a, b) => (b.created || 0) - (a.created || 0));
    });
    return groups;
  };

  const formatPriceCreated = (created) => {
    if (!created) return "—";
    const d = new Date(created * 1000);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const handleUseInConfig = async (priceId, productId) => {
    const opt = priceOptions.find((o) => o.id === priceId);
    const freq = opt ? getFrequencyFromStripePrice(opt) : "monthly";
    const kind = opt && isSpaceProduct(opt.productId) ? "m2" : "box";
    const payload = {
      product_kind: kind,
      stripe_price_id: priceId,
      stripe_product_id: productId || "",
      frequency: freq,
      is_active: true,
      notes: "",
    };
    try {
      setSaving(true);
      const existing = getConfigForPriceId(priceId);
      if (existing) {
        await axios.patch(`/admin/stripe/price-config/${existing._id}`, {
          is_active: payload.is_active,
          stripe_price_id: payload.stripe_price_id,
          stripe_product_id: payload.stripe_product_id,
          notes: payload.notes,
        });
        enqueueSnackbar(t("stripePricing.success.configUpdated"), { variant: "success" });
      } else {
        await axios.post("/admin/stripe/price-config", payload);
        enqueueSnackbar(t("stripePricing.success.configCreated"), { variant: "success" });
      }
      fetchConfigs();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || t("stripePricing.errors.save"), {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPriceId = (priceId) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(priceId);
      enqueueSnackbar(t("stripePricing.success.priceCopied"), { variant: "success" });
    }
  };

  const filteredProducts = listFilters.productId
    ? products.filter((pr) => pr.id === listFilters.productId)
    : products;

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        {t("stripePricing.title")}
      </Typography>
      <Button
        className={classes.purgeButton}
        variant="outlined"
        color="primary"
        onClick={handlePurgeCache}
        disabled={purging}
      >
        {purging ? t("stripePricing.purging") : t("stripePricing.purgeCache")}
      </Button>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={t("stripePricing.tabProductsPrices")} />
        <Tab label={t("stripePricing.tabConfigHistory")} />
        <Tab label={t("stripePricing.tabCreatePrice")} />
      </Tabs>

      <TabPanel value={tab} index={0} className={classes.tabPanel}>
        {loadingProducts ? (
          <Loading />
        ) : (
          <>
            <div className={classes.filterBar}>
              <FormControl size="small" className={classes.formControl} style={{ minWidth: 220 }}>
                <InputLabel>{t("stripePricing.product")}</InputLabel>
                <Select
                  value={listFilters.productId}
                  onChange={(e) => { const v = e.target.value; setListFilters((f) => ({ ...f, productId: v })); }}
                  label={t("stripePricing.product")}
                >
                  <MenuItem value="">{t("stripePricing.allProducts")}</MenuItem>
                  {products.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" className={classes.formControl} style={{ minWidth: 120 }}>
                <InputLabel>{t("stripePricing.status")}</InputLabel>
                <Select
                  value={listFilters.active}
                  onChange={(e) => { const v = e.target.value; setListFilters((f) => ({ ...f, active: v })); }}
                  label={t("stripePricing.status")}
                >
                  <MenuItem value="">{t("stripePricing.all")}</MenuItem>
                  <MenuItem value="yes">{t("stripePricing.activeOnly")}</MenuItem>
                  <MenuItem value="no">{t("stripePricing.archivedOnly")}</MenuItem>
                </Select>
              </FormControl>
            </div>
            {filteredProducts.map((product) => {
              let prices = product.prices || [];
              if (listFilters.active === "yes") prices = prices.filter((p) => p.active);
              if (listFilters.active === "no") prices = prices.filter((p) => !p.active);
              const byInterval = groupPricesByInterval(prices);
              return (
                <Card key={product.id} className={classes.productCard}>
                  <CardContent>
                    <Typography variant="h6">
                      {product.name} ({product.id})
                    </Typography>
                    {prices.length === 0 ? (
                      <Typography color="textSecondary" style={{ marginTop: 16 }}>
                        {t("stripePricing.noPricesMatch")}
                      </Typography>
                    ) : (
                      FREQUENCIES.map((freq) => {
                        const sectionPrices = byInterval[freq] || [];
                        if (sectionPrices.length === 0) return null;
                        return (
                          <div key={freq} className={classes.intervalSection}>
                            <Table size="small" className={classes.table}>
                              <TableHead>
                                <TableRow>
                                  <TableCell colSpan={5} className={classes.intervalSectionTitle}>
                                    {t(`stripePricing.${freq}`)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>{t("stripePricing.priceId")}</TableCell>
                                  <TableCell>{t("stripePricing.amountTiers")}</TableCell>
                                  <TableCell>{t("stripePricing.active")}</TableCell>
                                  <TableCell>{t("stripePricing.created")}</TableCell>
                                  <TableCell className={classes.actionCell}>{t("stripePricing.actions")}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sectionPrices.map((p) => {
                                  const config = getConfigForPriceId(p.id);
                                  const isArchived = !p.active;
                                  return (
                                    <TableRow
                                      key={p.id}
                                      className={isArchived ? classes.archivedRow : undefined}
                                    >
                                      <TableCell>
                                        <Typography variant="body2" component="span">
                                          {p.id}
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleCopyPriceId(p.id)}
                                          aria-label={t("stripePricing.copyPriceId")}
                                        >
                                          <CopyIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                      <TableCell>
                                        {formatPrice(p)}
                                        {p.billing_scheme === "tiered" && (
                                          <Box className={classes.tierTableWrap} mt={0.5}>
                                            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                                              {t("stripePricing.tieredPricing")}
                                            </Typography>
                                            {(p.tiers || []).length > 0 ? (
                                              <table className={classes.tierTable}>
                                                <thead>
                                                  <tr>
                                                    <th>{t("stripePricing.m2Range")}</th>
                                                    <th>{t("stripePricing.eurPerM2")}</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {formatTieredTiers(p).map((tierStr, i) => {
                                                    const colonIdx = tierStr.indexOf(": €");
                                                    const range = colonIdx >= 0 ? tierStr.slice(0, colonIdx).trim() : tierStr;
                                                    const eurPart = colonIdx >= 0 ? tierStr.slice(colonIdx + 3).replace("/m²", "").trim() : "";
                                                    return (
                                                      <tr key={i}>
                                                        <td>{range}</td>
                                                        <td>{eurPart ? `€${eurPart}` : "—"}</td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            ) : (
                                              <Typography variant="caption" color="textSecondary">
                                                {t("stripePricing.tiersNotLoaded")}
                                              </Typography>
                                            )}
                                          </Box>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {config ? (
                                          config.is_active ? (
                                            <Typography color="primary">{t("common.yes")}</Typography>
                                          ) : (
                                            <Typography color="textSecondary">{t("common.no")}</Typography>
                                          )
                                        ) : (
                                          <Typography variant="body2" color="textSecondary">
                                            —
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" color="textSecondary">
                                          {formatPriceCreated(p.created)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell className={classes.actionCell}>
                                        <Button
                                          size="small"
                                          startIcon={<UseConfigIcon />}
                                          onClick={() => handleUseInConfig(p.id, product.id)}
                                          disabled={saving}
                                        >
                                          {t("stripePricing.useInConfig")}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </>
        )}
      </TabPanel>

      <TabPanel value={tab} index={1} className={classes.tabPanel}>
        {loadingConfigs ? (
          <Loading />
        ) : (
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>{t("stripePricing.productKind")}</TableCell>
                <TableCell>{t("stripePricing.frequency")}</TableCell>
                <TableCell>{t("stripePricing.stripePriceId")}</TableCell>
                <TableCell>{t("stripePricing.active")}</TableCell>
                <TableCell>{t("stripePricing.created")}</TableCell>
                <TableCell>{t("stripePricing.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((row) => (
                <TableRow key={row._id}>
                  <TableCell>{row.product_kind}</TableCell>
                  <TableCell>{row.frequency}</TableCell>
                  <TableCell>{row.stripe_price_id}</TableCell>
                  <TableCell>{row.is_active ? t("common.yes") : t("common.no")}</TableCell>
                  <TableCell>
                    {row.created_at
                      ? new Date(row.created_at).toLocaleDateString()
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2} className={classes.tabPanel}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t("stripePricing.createPriceIntro")}
        </Typography>
        {lastCreatedPriceId && (
          <Typography color="primary" style={{ marginBottom: 8 }}>
            {t("stripePricing.lastCreated")} <strong>{lastCreatedPriceId}</strong>
          </Typography>
        )}
        <div className={classes.createPriceForm}>
          <div className={classes.formRow}>
            <FormControl className={classes.formControl} style={{ minWidth: 280 }}>
              <InputLabel>{t("stripePricing.stripeProduct")}</InputLabel>
              <Select
                value={createPriceForm.product}
                onChange={(e) => handleCreatePriceFormChange("product", e.target.value)}
                label={t("stripePricing.stripeProduct")}
              >
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} ({p.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>{t("stripePricing.frequency")}</InputLabel>
              <Select
                value={createPriceForm.frequency}
                onChange={(e) => handleCreatePriceFormChange("frequency", e.target.value)}
                label={t("stripePricing.frequency")}
              >
                {FREQUENCIES.map((f) => (
                  <MenuItem key={f} value={f}>
                    {t(`stripePricing.${f}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          {(() => {
            const selectedProductId = createPriceForm.product;
            const showTiers = selectedProductId && isSpaceProduct(selectedProductId);

            return showTiers ? (
            <div className={classes.tierSection}>
              <div>
                <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                  {t("stripePricing.fixedSpaceTiers")}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                  {t("stripePricing.m2RangesFixed")}
                </Typography>
                {createPriceForm.tiers.map((tier, idx) => (
                  <div key={idx} className={classes.tierRow}>
                    <TextField
                      label={t("stripePricing.upToM2")}
                      value={tier.up_to_m2 || "∞"}
                      disabled
                      style={{ width: 120 }}
                      size="small"
                    />
                    <TextField
                      label={t("stripePricing.pricePerM2")}
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={tier.price_per_m2_euro}
                      onChange={(e) => handleTierChange(idx, "price_per_m2_euro", e.target.value)}
                      style={{ width: 140 }}
                      size="small"
                    />
                  </div>
                ))}
              </div>
              <div className={classes.tierExample}>
                <div className={classes.tierExampleTitle}>{t("stripePricing.example")}</div>
                <div style={{ marginBottom: 8 }}>
                  <strong>{t("stripePricing.quantityExample")}</strong>
                </div>
                <div style={{ marginBottom: 4 }}><strong>{t("stripePricing.rowsYouEnter")}</strong></div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "2px 6px 4px 0" }}>{t("stripePricing.upToM2")}</th>
                      <th style={{ textAlign: "left", padding: "2px 6px 4px 0" }}>{t("stripePricing.priceM2")}</th>
                      <th style={{ textAlign: "left", padding: "2px 0 4px 0" }}>Stripe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ padding: "2px 6px 2px 0" }}>3</td><td style={{ padding: "2px 6px 2px 0" }}>€13</td><td style={{ padding: "2px 0" }}>up_to: 30</td></tr>
                    <tr><td style={{ padding: "2px 6px 2px 0" }}>10</td><td style={{ padding: "2px 6px 2px 0" }}>€12</td><td style={{ padding: "2px 0" }}>up_to: 100</td></tr>
                    <tr><td style={{ padding: "2px 6px 2px 0" }}><em>{t("stripePricing.lastRow")}</em></td><td style={{ padding: "2px 6px 2px 0" }}>€11</td><td style={{ padding: "2px 0" }}>up_to: inf</td></tr>
                  </tbody>
                </table>
                <div style={{ marginTop: 8, color: "inherit", opacity: 0.9 }}>
                  {t("stripePricing.lastRowNote")}
                </div>
              </div>
            </div>
          ) : createPriceForm.product ? (
            <div className={classes.formRow} style={{ marginTop: 16 }}>
              <TextField
                label={t("stripePricing.unitAmount")}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={createPriceForm.unit_amount_euro}
                onChange={(e) => handleCreatePriceFormChange("unit_amount_euro", e.target.value)}
                className={classes.formControl}
                style={{ minWidth: 160 }}
                helperText={t("stripePricing.flatPricePerBox")}
              />
            </div>
          ) : null;
          })()}
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreatePrice}
            disabled={creatingPrice}
            style={{ marginTop: 24 }}
          >
            {creatingPrice ? t("stripePricing.creating") : t("stripePricing.createPriceInStripe")}
          </Button>
        </div>
      </TabPanel>
    </div>
  );
};

export default StripePricing;
